import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { parseCSV, parseXLSX } from "../../utils/bulkUploadUtils";
import { Button, Text } from "../../components";
import { UserManagementContainer } from "../userManagement/UserManagementContainer";
import { AlertIcon, CloseIcon, Spinner, UploadIcon } from "../../components/icons/Icons";
import { List } from "../../components/List/List";
import { FileUpload } from "../../components/fileUpload/FileUpload";
import "./BulkUpload.scss";
import { bulkUploadUserRequest } from "../../services/users";
import UserExcelExporter from "../../utils/UserExcelExporter";
import { BulkUploadUserRequest } from "../../models/BulkUploadUserRequest";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, fetchSettings } from "../../redux/internal";
import { downloadFile } from "../../utils/downloadFile";

export const BulkUpload = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [toastType, setToastType] = useState<string>("");
  const [headerFormat, setHeaderFormat] = useState<string[]>([]);
  const [isStore, setIsStore] = useState<boolean>(false);
  const [isApplication, setIsApplication] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [file, setFile] = useState<string>("");
  const [userType, setUserType] = useState<string>("");
  const [failedRecords, setFailedRecords] = useState<string[]>([]);
  const [userData, setUserData] = useState<BulkUploadUserRequest[]>([]);
  const { settings } = useSelector((state: RootState) => state.settings);
  const dispatch = useDispatch<AppDispatch>();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const maxNumberRecords = (settings && settings?.webApp?.bulkUser?.maxAllowedLimit) ?? 100;
  const templateFiles = (settings && settings?.webApp?.bulkUser?.templates) ?? [];
  const alertPermissionsConfigured = (settings?.webApp?.bulkUser?.alertPermissionMappings?.length ?? 0) > 0;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    file && handleFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files?.length > 1) {
      setToastType("failure");
      setAlertMessage("multipleFileUploadErrorMessage");
    } else {
      const droppedFiles = event.dataTransfer.files?.[0];
      droppedFiles && handleFile(droppedFiles);
    }
  };

  const getFileType = (fileName: string) => {
    const fileType = fileName?.split(".").pop()?.toLocaleLowerCase();
    if (fileType !== "csv" && fileType !== "xlsx" && fileType !== "xls") {
      return null;
    } else {
      return fileType;
    }
  };

  const handleFile = (file: File) => {
    setAlertMessage("");
    if (file) {
      const fileName = file.name;
      setFile(file.name);
      const fileType = getFileType(fileName);
      if (!fileType) {
        setToastType("failure");
        setAlertMessage("fileTypeError");
        return;
      }
      const reader = new FileReader();
      if (fileType === "csv") {
        reader.readAsText(file);
      } else if (fileType === "xlsx" || fileType === "xls") {
        reader.readAsArrayBuffer(file);
      }
      reader.onload = (event) => {
        const result = event?.target?.result;
        if (result) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let jsonData: any[] = [];
          if (fileType === "csv") {
            const csvParsed = parseCSV(result as string, setIsStore, setIsApplication, alertPermissionsConfigured);
            jsonData = csvParsed.result;
            setHeaderFormat(csvParsed.headerFormat);
            setUserType(csvParsed.userType);
          } else if (fileType === "xlsx" || fileType === "xls") {
            const xlsxParsed = parseXLSX(
              result as ArrayBuffer,
              setIsStore,
              setIsApplication,
              alertPermissionsConfigured,
            );
            jsonData = xlsxParsed.processedData;
            setHeaderFormat(xlsxParsed.headerFormat);
            setUserType(xlsxParsed.userType);
          }
          if (!jsonData?.length) {
            setToastType("failure");
            setAlertMessage("wrongFileData");
            return;
          }
          if (jsonData?.length >= maxNumberRecords) {
            setToastType("failure");
            setAlertMessage("recordLimitExceed");
            return;
          }
          setUserData(jsonData);
          setToastType("success");
          setAlertMessage(`${fileName} template ready for upload.`);
        }
      };
    }
  };

  const handleBulkUpload = async () => {
    setLoading(true);
    const response = await bulkUploadUserRequest(userType, userData);
    setIsFileUploaded(true);
    if (response === true) {
      setToastType("success");
      setAlertMessage(`${file} User updates successfully completed.`);
    } else if (response === false) {
      setToastType("failure");
      setAlertMessage("errorUploadingFile");
    } else {
      setToastType("warning");
      setFailedRecords(response);
      setAlertMessage("unsuccessfulFileUpload");
      UserExcelExporter(response, headerFormat, isStore, isApplication);
    }
    setLoading(false);
  };

  useEffect(() => {
    !settings && void dispatch(fetchSettings());
  }, [settings, dispatch]);

  return (
    <UserManagementContainer>
      {loading ? (
        <div className="spinner-overlay">
          <div className="spinner-container">
            <Spinner />
          </div>
        </div>
      ) : null}
      <div className="bulk-upload">
        <div className="upload-container">
          <div className="instruction-container">
            <div>
              <AlertIcon />
              <Text lKey={t("bulkUploadInstruction")} className="title" />
            </div>
            <Text lKey={t("bulkUploadInstructionHeading1")} className="heading" />
            <List
              className="instructions"
              type="ordered"
              list={["bulkUploadOrderedInstruction1", "bulkUploadOrderedInstruction2", "bulkUploadOrderedInstruction3"]}
            />
            <Text lKey={t("bulkUploadInstructionHeading2")} className="heading2" />
            <List
              className="instructions"
              list={[
                "bulkUploadUnorderedInstruction1",
                "bulkUploadUnorderedInstruction2",
                "bulkUploadUnorderedInstruction3",
              ]}
            />
            <div className="user-templates">
              <Text lKey={t("userTemplates")} className="template-heading" />
              <div className="button-container">
                <Button
                  className={`user-button "active" : ""}`}
                  isDisabled={false}
                  onClick={() => {
                    downloadFile(templateFiles[1]?.filePath, "email.xlsx");
                  }}
                  type="button"
                >
                  <Text className="font" lKey={"Users with"} />
                  <Text className="bold-font" lKey={"email address"} />
                </Button>
                <Button
                  className={`user-button "active" : ""`}
                  isDisabled={false}
                  onClick={() => {
                    downloadFile(templateFiles[0]?.filePath, "baisc.xlsx");
                  }}
                  type="button"
                >
                  <Text className="font" lKey={"Users"} />
                  <Text className="bold-font" lKey={"without email address"} />
                </Button>
                <Button
                  className={`user-button "active" : ""`}
                  isDisabled={false}
                  onClick={() => {
                    downloadFile(templateFiles[2]?.filePath, "sso.xlsx");
                  }}
                  type="button"
                >
                  <Text className="bold-font" lKey={"SSO"} />
                  <Text className="font" lKey={"users"} />
                </Button>
              </div>
            </div>
          </div>
          <div className={"file-upload-container"} onDrop={handleDrop} onDragOver={(event) => event.preventDefault()}>
            <FileUpload
              className="file-upload"
              inputText={t("chooseFile")}
              textMsg="dragHere"
              Icon={UploadIcon}
              onChange={handleFileUpload}
            />
          </div>
          {toastType === "success" && alertMessage && (
            <div className={"toast-container"}>
              <div>
                <div>
                  <AlertIcon />
                  <Text className={"toast-message"} lKey={t(alertMessage)} />
                </div>
                {!isFileUploaded && <CloseIcon onClick={() => setToastType("")} />}
              </div>
            </div>
          )}
          {(toastType === "failure" || toastType === "warning") && alertMessage && (
            <div className={`toast-container ${toastType === "warning" ? "warning" : "error"}`}>
              <div>
                <div>
                  <AlertIcon />
                  <Text className={"toast-message"} lKey={t(alertMessage, { placeholder: file })} />
                </div>
              </div>
              {toastType === "warning" && (
                <span className="warning-message">
                  A file containing the errors has been downloaded onto your device. You can also&nbsp;
                  <button
                    className={"download-file"}
                    onClick={() => UserExcelExporter(failedRecords, headerFormat, isStore, isApplication)}
                  >
                    click here
                  </button>
                  &nbsp;to re-download. Once these are fixed please re-upload.
                </span>
              )}
              <Text
                className={"toast-error"}
                lKey={toastType === "failure" && isFileUploaded ? t("errorBulkUserFileUploading") : ""}
              />
            </div>
          )}
        </div>
        <div className="bulk-upload-buttons">
          {isFileUploaded ? (
            <Button
              className="button-small primary-color user-managemnt-button"
              isDisabled={false}
              lKey={t("buttonUserManagement")}
              onClick={() => navigate("/user-management")}
              type="button"
            />
          ) : (
            <>
              <Button
                className="button-small"
                isDisabled={false}
                lKey={"buttonCancel"}
                onClick={() => navigate("/user-management")}
                type="button"
              />
              <Button
                className="button-small primary-color"
                isDisabled={toastType !== "success"}
                lKey={"buttonUpload"}
                onClick={handleBulkUpload}
                type="submit"
              />
            </>
          )}
        </div>
      </div>
    </UserManagementContainer>
  );
};
